import React from 'react';
import { Box, Paper, Typography, Skeleton, Divider, Stack, Card } from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import LockIcon from '@mui/icons-material/Lock';
import { useTranslation } from 'react-i18next';
import { useFormatter } from '../../../context/FormatterContext';

interface TaxDataItem {
    label: string;
    amount: number;
    translationKey: string;
}

interface TaxFreeIncomeBoxProps {
    taxFreeIncome: TaxDataItem[];
    isPaid: boolean;
    isLoading?: boolean;
}

const TaxFreeIncomeBox: React.FC<TaxFreeIncomeBoxProps> = ({
    taxFreeIncome = [],
    isPaid,
    isLoading
}) => {
    const { t } = useTranslation();
    const { formatCurrency } = useFormatter();

    // Default data structure
    const defaultTaxFreeIncome: TaxDataItem[] = [
        { label: 'Non-taxable sales income', amount: 0, translationKey: 'taxReports.nonTaxableSales' },
        { label: 'Non-taxable other income', amount: 0, translationKey: 'taxReports.nonTaxableOther' }
    ];

    const displayTaxFreeIncome = taxFreeIncome.length > 0 ? taxFreeIncome : defaultTaxFreeIncome;

    // Get icon for each category
    const getCategoryIcon = (index: number) => {
        const icons = [
            <MoneyOffIcon sx={{ color: '#4CAF50', fontSize: 20 }} />,
            <BusinessCenterIcon sx={{ color: '#FF9800', fontSize: 20 }} />
        ];
        return icons[index] || icons[0];
    };

    const formatAmount = (value: number | null) => {
        if (value === null || value === undefined) return '---';
        return formatCurrency(value);
    };

    return (
        <Card sx={{}}>
            {/* Header */}
            <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2 }}>
                    <AccountBalanceWalletIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="h6">
                        {t('taxReports.taxFreeIncome', 'Steuerfreie Einkünfte')}
                    </Typography>
                </Box>
            </Box>

            {/* Data Items */}
            <Box sx={{ p: 2 }}>
                <Stack spacing={2}>
                    {displayTaxFreeIncome.map((item, index) => (
                        <Box key={index} sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            py: 1,
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                {getCategoryIcon(index)}
                                <Typography variant="body1">
                                    {t(item.translationKey, item.label)}
                                </Typography>
                            </Box>

                            {isLoading ? (
                                <Skeleton width="100px" height="24px" />
                            ) : isPaid ? (
                                <Typography variant="body1" color="text.primary">
                                    {formatAmount(item.amount)}
                                </Typography>
                            ) : (
                                <LockIcon sx={{ 
                                    color: 'text.secondary', 
                                    fontSize: 20 
                                }} />
                            )}
                        </Box>
                    ))}
                </Stack>
            </Box>
        </Card>
    );
};

export default TaxFreeIncomeBox;